/**
 * <order-modification> — page « Modifier ma commande ».
 *
 * Numéro + email → le backend vérifie (tag TO_PRODUCE côté serveur) et renvoie
 * les lignes personnalisées. Chaque design s'ouvre dans l'éditeur (?d=…) et se
 * met à jour EN PLACE : la commande imprimera la nouvelle version.
 * Au retour de l'éditeur, la recherche se rejoue seule (sessionStorage).
 */

const LOOKUP_KEY = 'lfc-modify-order:v1';

class OrderModification extends HTMLElement {
  connectedCallback() {
    this.editorBase = this.dataset.editorBase || 'https://template.lesfrereschaussettes.com';
    this.refs = {};
    for (const element of this.querySelectorAll('[data-ref]')) {
      this.refs[element.dataset.ref] = element;
    }

    this.refs['lookup-form'].addEventListener('submit', (event) => {
      event.preventDefault();
      this.#lookup();
    });
    this.refs['restart'].addEventListener('click', () => {
      try { sessionStorage.removeItem(LOOKUP_KEY); } catch {}
      this.refs['result'].hidden = true;
      this.refs['lookup-form'].hidden = false;
      this.refs['order-number'].value = '';
      this.refs['email'].value = '';
    });

    this.#consumeEditorReturn();
  }

  /** Retour d'éditeur : ?d présent → rejoue la recherche et confirme. */
  #consumeEditorReturn() {
    const params = new URLSearchParams(window.location.search);
    const cameBack = params.has('d');
    if (cameBack) {
      const url = new URL(window.location.href);
      url.searchParams.delete('d');
      url.searchParams.delete('thumb');
      history.replaceState({}, '', url.toString());
    }
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(LOOKUP_KEY) || 'null'); } catch {}
    if (saved?.orderNumber && saved?.email) {
      this.refs['order-number'].value = saved.orderNumber;
      this.refs['email'].value = saved.email;
      this.#lookup({ showUpdated: cameBack });
    }
  }

  async #lookup({ showUpdated = false } = {}) {
    const orderNumber = this.refs['order-number'].value.trim();
    const email = this.refs['email'].value.trim();
    const error = this.refs['error'];
    const submit = this.refs['submit'];
    error.hidden = true;
    submit.disabled = true;
    submit.textContent = 'Recherche…';

    try {
      const response = await fetch(`${this.editorBase}/order-modification/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNumber, email }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Vérification impossible pour le moment.');

      try { sessionStorage.setItem(LOOKUP_KEY, JSON.stringify({ orderNumber, email })); } catch {}
      this.#renderResult(payload, { showUpdated });
    } catch (err) {
      error.textContent = err.message;
      error.hidden = false;
    } finally {
      submit.disabled = false;
      submit.textContent = 'Retrouver ma commande';
    }
  }

  #renderResult(payload, { showUpdated }) {
    this.refs['lookup-form'].hidden = true;
    this.refs['result'].hidden = false;
    this.refs['updated-banner'].hidden = !showUpdated;

    const status = this.refs['status'];
    if (payload.modifiable) {
      status.className = 'modify-order__status modify-order__status--open';
      status.innerHTML = `Commande ${this.#escape(payload.orderName)} — tes designs sont encore modifiables ✏️<small>Dès que la production démarre, la commande part telle quelle : ne tarde pas.</small>`;
    } else {
      status.className = 'modify-order__status modify-order__status--closed';
      const reason = payload.reason === 'expediee'
        ? `Commande ${this.#escape(payload.orderName)} — déjà expédiée, la modification n'est plus possible.`
        : `Commande ${this.#escape(payload.orderName)} — déjà en production à l'atelier, la modification n'est plus possible.`;
      status.innerHTML = `${reason}<small>Un souci sur ta commande ? Écris-nous via la <a href="/pages/contact">page contact</a>, on trouve une solution.</small>`;
    }

    const items = this.refs['items'];
    items.innerHTML = '';
    if (!payload.items.length) {
      items.innerHTML = `<p style="opacity:.7">Cette commande ne contient pas de paires personnalisées.</p>`;
      return;
    }
    for (const item of payload.items) {
      const card = document.createElement('div');
      card.className = 'modify-order__item';
      const canEdit = payload.modifiable && item.designId && item.templateId;
      const origin = `${window.location.origin}${window.location.pathname}`;
      const editUrl = canEdit
        ? `${this.editorBase}/use/${encodeURIComponent(item.templateId)}?d=${encodeURIComponent(item.designId)}&origin=${encodeURIComponent(origin)}`
        : null;
      const thumbs = (item.thumbnails || []).map((src) => `<img src="${this.#escape(src)}" alt="Aperçu du design" loading="lazy">`).join('')
        || (item.imageUrl ? `<img src="${this.#escape(item.imageUrl)}" alt="" loading="lazy">` : '');
      card.innerHTML = `
        <div class="modify-order__item-head">
          <span class="modify-order__item-title">${this.#escape(item.title)}<small>${this.#escape(item.sku || '')}${item.quantity > 1 ? ` · × ${item.quantity}` : ''}</small></span>
          ${editUrl ? `<a class="button button--primary button--small" href="${editUrl}">✏️ Modifier ce design</a>` : ''}
        </div>
        <div class="modify-order__thumbs">${thumbs}</div>`;
      items.appendChild(card);
    }
  }

  #escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }
}

if (!customElements.get('order-modification')) {
  customElements.define('order-modification', OrderModification);
}
